import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Text,
  FormControl,
  FormLabel,
  Input,
  VStack,
} from '@chakra-ui/react';

interface ConfirmCrossListModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (data?: { price: number; quantity: number }) => void;
  isLoading?: boolean;
  listingTitle: string;
  isBuyNowConversion?: boolean;
  isRetry?: boolean;
}

const ConfirmCrossListModal: React.FC<ConfirmCrossListModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  isLoading = false,
  listingTitle,
  isBuyNowConversion = false,
  isRetry = false,
}) => {
  const [price, setPrice] = useState<string>('');
  const [quantity, setQuantity] = useState<string>('');

  const handleConfirm = () => {
    if (isBuyNowConversion) {
      const priceNum = parseFloat(price);
      const quantityNum = parseInt(quantity, 10);
      onConfirm({ price: priceNum, quantity: quantityNum });
    } else {
      onConfirm();
    }
  };

  const isValid =
    !isBuyNowConversion ||
    (price !== '' &&
      !isNaN(parseFloat(price)) &&
      parseFloat(price) > 0 &&
      quantity !== '' &&
      !isNaN(parseInt(quantity, 10)) &&
      parseInt(quantity, 10) > 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Confirm Cross-List to eBay</ModalHeader>
        <ModalBody>
          <VStack align="start" spacing={3}>
            <Text>
              {isRetry
                ? 'This will retry cross-listing this item to eBay. Check back on the listing details page for status updates.'
                : isBuyNowConversion
                  ? 'This will create a Buy Now listing and cross-list it to eBay. Check back on the listing details page for status updates.'
                  : 'This will cross-list this item to eBay. Check back on the listing details page for status updates.'}
            </Text>
            <Text fontWeight="semibold">{listingTitle}</Text>

            {isBuyNowConversion && (
              <>
                <FormControl isRequired>
                  <FormLabel>Price</FormLabel>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    placeholder="Enter price"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    isDisabled={isLoading}
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Quantity</FormLabel>
                  <Input
                    type="number"
                    min="1"
                    placeholder="Enter quantity"
                    value={quantity}
                    onChange={(e) => setQuantity(e.target.value)}
                    isDisabled={isLoading}
                  />
                </FormControl>
              </>
            )}

            <Text fontSize="sm" color="gray.600">
              Are you sure you want to proceed?
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={isLoading}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleConfirm}
            isLoading={isLoading}
            isDisabled={isLoading || !isValid}
          >
            Confirm
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ConfirmCrossListModal;
